import React, { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import {
    Typography,
    Box,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Switch,
    Stack,
    createTheme,
    ThemeProvider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
// import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'; // WhatsApp option (commented)
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ISwitchWithCustomText } from '../Switch/SwitchWithCustomText';
import { IUserConfiguration, WebCommunicationPreference } from '../../redux/configuration/types';
import { useDispatch } from 'react-redux';
import { toggleAccountModal } from '../../redux/appConfigurator/action';
import { communication } from '../EsSettings/settings.data';

export interface ISettingsProps extends Omit<ISwitchWithCustomText, 'header' | 'body' | 'theme'> {
    onChange: (payload: any) => void;
    setting: IUserConfiguration;
}

type PreferredVerificationValue = 'whatsapp' | 'sms' | 'email' | 'audme';

interface PreferredVerificationOption {
    value: PreferredVerificationValue;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    disabled: boolean;
    badge?: string;
}

const AccountCommunications: React.FC<ISettingsProps> = props => {
    const { onChange, setting } = props;
    const [settingValue, setSettingsValue] = useState<any>();

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(toggleAccountModal({ on: false }));

        if (setting) {
            setSettingsValue({
                web_communication_preference: setting.web_communication_preference,
                web_notifications_settings: setting.web_notifications_settings,
                preferred_verification: setting.preferred_verification || 'whatsapp',
                appConfig: {
                    show_app_guide: setting.show_app_guide,
                    show_introduction: setting.show_introduction,
                    show_project_guide: setting.show_project_guide,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);

    const switchTheme = createTheme({
        components: {
            MuiSwitch: {
                styleOverrides: {
                    switchBase: {
                        // Controls default (unchecked) color for the thumb
                        color: theme[themeStyle].tag_white,
                    },
                    colorPrimary: {
                        '&.Mui-checked': {
                            // Controls checked color for the thumb
                            color: theme[themeStyle].tag_white,
                        },
                    },
                    track: {
                        // Controls default (unchecked) color for the track
                        opacity: 1,
                        backgroundColor: theme[themeStyle].gray_message,
                        '.Mui-checked.Mui-checked + &': {
                            // Controls checked color for the track
                            opacity: 0.9,
                            backgroundColor: theme[themeStyle].highlight,
                        },
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        color: theme[themeStyle].gray_message,
                        '&.Mui-checked': {
                            color: theme[themeStyle].highlight,
                        },
                    },
                },
            },
            //@ts-ignore
            custom: {},
        },
    });

    const handleOnToggle = (value: boolean, child: string, parent: string) => {
        setSettingsValue({
            ...settingValue,
            [parent]: {
                ...settingValue[parent],
                [child]: value,
            },
        });
        if (parent === 'appConfig') {
            onChange({
                [child]: value,
            });
        } else {
            onChange({
                ...settingValue,
                [parent]: {
                    ...settingValue[parent],
                    [child]: value,
                },
            });
        }
    };

    const handlePreferredVerificationChange = (value: string) => {
        setSettingsValue({
            ...settingValue,
            preferred_verification: value,
        });
        onChange({
            preferred_verification: value,
        });
    };

    const communicationRowIcon = (serviceName: string) => {
        const sx = { fontSize: 26 as const };
        switch (serviceName) {
            case 'phone':
                return <PhoneOutlinedIcon sx={sx} />;
            case 'sms':
                return <SmsOutlinedIcon sx={sx} />;
            case 'email':
                return <AlternateEmailOutlinedIcon sx={sx} />;
            default:
                return <PhoneOutlinedIcon sx={sx} />;
        }
    };

    const storedPreferredVerification = settingValue?.preferred_verification || 'audme';
    const verificationRadioValue: PreferredVerificationValue =
        storedPreferredVerification === 'whatsapp' || storedPreferredVerification === 'sms'
            ? 'audme'
            : storedPreferredVerification;

    return (
        <ThemeProvider theme={switchTheme}>
            <Box
                style={styles.flexColumn}
                sx={{
                    gap: 3,
                    width: '100%',
                    height: '100%',
                    p: {
                        xs: 2,
                        sm: 0,
                    },
                }}>
                <Box
                    style={styles.flexColumn}
                    sx={{
                        gap: 4,
                        width: '100%',
                    }}>
                    <Box style={styles.flexColumn} sx={{ gap: '10px', maxWidth: '454px' }}>
                        <Typography
                            sx={{
                                fontSize: '0.8125rem',
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].tag_white,
                                opacity: 0.65,
                                lineHeight: 1.5,
                                maxWidth: '36rem',
                            }}>
                            Choose how we can contact you. You will only be contacted through your chosen methods.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            width: '100%',
                        }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                            {settingValue &&
                                settingValue.web_communication_preference &&
                                communication.map((set, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            flexWrap: 'wrap',
                                            p: 3,
                                            borderRadius: 3,
                                            border: '1px solid rgba(255,255,255,0.06)',
                                            bgcolor: 'rgba(39, 39, 42, 0.35)',
                                            transition: 'background-color 0.2s ease',
                                            '&:hover': { bgcolor: 'rgba(39, 39, 42, 0.5)' },
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                minWidth: 0,
                                                flex: 1,
                                            }}>
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 2,
                                                    flexShrink: 0,
                                                    bgcolor: 'rgba(0,0,0,0.28)',
                                                    border: '1px solid rgba(255,255,255,0.06)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: theme[themeStyle].highlight,
                                                }}>
                                                {communicationRowIcon(set.service_name)}
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontWeight: 700,
                                                        fontSize: '0.9375rem',
                                                        color: theme[themeStyle].primary_white,
                                                        mb: 0.25,
                                                    }}>
                                                    {set.header}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                        color: theme[themeStyle].tag_white,
                                                        opacity: 0.65,
                                                        lineHeight: 1.4,
                                                    }}>
                                                    {set.body}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Switch
                                            focusVisibleClassName=".Mui-focusVisible"
                                            disableRipple
                                            checked={Boolean(
                                                settingValue.web_communication_preference[
                                                    set.service_name as keyof WebCommunicationPreference
                                                ],
                                            )}
                                            onChange={e =>
                                                handleOnToggle(e.target.checked, set.service_name, set.parent_name)
                                            }
                                            sx={{
                                                flexShrink: 0,
                                            }}
                                        />
                                    </Box>
                                ))}
                        </Box>
                    </Box>
                </Box>

                {/* Preferred Verification Section */}
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        width: '100%',
                        bgcolor: 'rgba(39, 39, 42, 0.35)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: '2rem',
                        p: { xs: 3, md: 5 },
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.45)',
                    }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -96,
                            right: -96,
                            width: 256,
                            height: 256,
                            borderRadius: '50%',
                            // bgcolor: alpha(theme[themeStyle].highlight, 0.06),
                            //  filter: 'blur(80px)',
                            pointerEvents: 'none',
                        }}
                    />
                    <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                            <Box
                                sx={{
                                    bgcolor: alpha(theme[themeStyle].highlight, 0.12),
                                    p: 1.5,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                <VerifiedIcon
                                    sx={{
                                        fontSize: 32,
                                        color: theme[themeStyle].highlight,
                                    }}
                                />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        fontSize: '1.25rem',
                                        letterSpacing: '-0.02em',
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Preferred Verification Method
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.8125rem',
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                        opacity: 0.65,
                                        lineHeight: 1.5,
                                        maxWidth: '36rem',
                                        mt: 0.5,
                                    }}>
                                    Choose how we can contact you. You will only be contacted through your chosen
                                    methods.
                                </Typography>
                            </Box>
                        </Stack>

                        <FormControl component="fieldset" variant="standard" sx={{ width: '100%', m: 0 }}>
                            <RadioGroup
                                value={verificationRadioValue}
                                onChange={e => handlePreferredVerificationChange(e.target.value)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    width: '100%',
                                    mt: 2,
                                }}>
                                {(
                                    [
                                        // {
                                        //     value: 'whatsapp',
                                        //     title: 'WhatsApp',
                                        //     subtitle: 'Recommended for instant updates',
                                        //     icon: <ChatBubbleOutlineIcon sx={{ fontSize: 22 }} />,
                                        //     disabled: true,
                                        // },
                                        // {
                                        //     value: 'sms',
                                        //     title: 'SMS',
                                        //     subtitle: 'Direct mobile messaging',
                                        //     icon: <SmsOutlinedIcon sx={{ fontSize: 22 }} />,
                                        //     disabled: true,
                                        // },
                                        {
                                            value: 'email',
                                            title: 'Email',
                                            subtitle: 'Formal and documented',
                                            icon: <MailOutlineIcon sx={{ fontSize: 22 }} />,
                                            disabled: false,
                                        },
                                        {
                                            value: 'audme',
                                            title: 'Audme App',
                                            subtitle: 'In-app push notifications',
                                            icon: <PhoneIphoneIcon sx={{ fontSize: 22 }} />,
                                            disabled: false,
                                            badge: 'Secure',
                                        },
                                    ] satisfies PreferredVerificationOption[]
                                ).map(opt => (
                                    <FormControlLabel
                                        key={opt.value}
                                        value={opt.value}
                                        disabled={opt.disabled}
                                        labelPlacement="start"
                                        control={
                                            <Radio
                                                disableRipple
                                                sx={{
                                                    p: 1,
                                                    color: theme[themeStyle].gray_message,
                                                    '&.Mui-checked': {
                                                        color: theme[themeStyle].highlight,
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2.5,
                                                    flex: 1,
                                                    minWidth: 0,
                                                    py: 0.25,
                                                }}>
                                                <Box
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: '50%',
                                                        flexShrink: 0,
                                                        bgcolor: 'rgba(0,0,0,0.28)',
                                                        border: '1px solid rgba(255,255,255,0.06)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: theme[themeStyle].tag_white,
                                                        transition: 'transform 0.2s ease, color 0.2s ease',
                                                    }}>
                                                    {opt.icon}
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            flexWrap: 'wrap',
                                                        }}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                fontWeight: 700,
                                                                fontSize: '1rem',
                                                                color: theme[themeStyle].primary_white,
                                                            }}>
                                                            {opt.title}
                                                        </Typography>
                                                        {opt.badge && (
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    fontSize: '9px',
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    fontWeight: 700,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.12em',
                                                                    color: theme[themeStyle].highlight,
                                                                    bgcolor: alpha(theme[themeStyle].highlight, 0.2),
                                                                    px: 1,
                                                                    py: 0.25,
                                                                    borderRadius: 999,
                                                                }}>
                                                                {opt.badge}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.75rem',
                                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                            color: theme[themeStyle].tag_white,
                                                            opacity: 0.65,
                                                            lineHeight: 1.4,
                                                        }}>
                                                        {opt.subtitle}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        }
                                        sx={{
                                            width: '100%',
                                            maxWidth: '100%',
                                            m: 0,
                                            ml: 0,
                                            mr: 0,
                                            px: 2.5,
                                            py: 2.5,
                                            boxSizing: 'border-box',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            borderRadius: 4,
                                            border: '1px solid rgba(255,255,255,0.06)',
                                            bgcolor: 'rgba(39, 39, 42, 0.35)',
                                            transition: 'background-color 0.2s ease',
                                            cursor: opt.disabled ? 'default' : 'pointer',
                                            '&:hover': {
                                                bgcolor: opt.disabled
                                                    ? 'rgba(39, 39, 42, 0.35)'
                                                    : 'rgba(39, 39, 42, 0.5)',
                                            },
                                            ...(opt.disabled ? { opacity: 0.55 } : {}),
                                            '& .MuiFormControlLabel-label': {
                                                flex: 1,
                                                minWidth: 0,
                                                marginRight: 1,
                                            },
                                            '& .MuiButtonBase-root.MuiRadio-root': {
                                                flexShrink: 0,
                                            },
                                        }}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
        },
        flexRowBetween: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        linebreak: {
            width: '100%',
            border: `1px solid ${theme[colorScheme].gray_text}`,
        },
    });

export { AccountCommunications };
